"use client";

import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CircleCheck, Send } from "lucide-react";
import { track } from "@/lib/analytics";
import { DEFAULT_WHATSAPP_MESSAGE, whatsappUrl } from "@/lib/whatsapp";
import { CONTACT_SEGMENT_EVENT, readContactSegment } from "@/lib/contact-intent";
import type { SegmentId } from "@/data/segments";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";

const segmentLabel: Record<SegmentId, string> = {
  sports: "Sports",
  education: "Education",
  retail: "Retail",
  traffic: "Traffic",
};

export default function WhatsAppContactChat() {
  const [segment, setSegment] = React.useState<SegmentId | null>(null);
  const [name, setName] = React.useState("");
  const [sent, setSent] = React.useState(false);

  React.useEffect(() => {
    const pre = readContactSegment();
    if (pre) setSegment(pre);
    const onSegment = (e: Event) => setSegment((e as CustomEvent<SegmentId>).detail);
    window.addEventListener(CONTACT_SEGMENT_EVENT, onSegment);
    return () => window.removeEventListener(CONTACT_SEGMENT_EVENT, onSegment);
  }, []);

  const greeting = segment
    ? `Olá! 👋 Vi que você tem interesse em falar sobre ${segmentLabel[segment]}. Deixe seu nome e toque no botão abaixo para começarmos a conversa.`
    : "Olá! 👋 Deixe seu nome e toque no botão abaixo para começarmos a conversa com nosso time.";

  const message = (() => {
    const who = name.trim();
    const parts = [
      who ? `Olá! Meu nome é ${who}.` : "Olá!",
      DEFAULT_WHATSAPP_MESSAGE,
      segment ? `Quero falar sobre ${segmentLabel[segment]}.` : undefined,
    ].filter(Boolean);
    return parts.join(" ");
  })();

  const send = () => {
    track("form_submit", {
      form: "contact_whatsapp",
      segment: segment ? segmentLabel[segment] : undefined,
    });
    window.open(whatsappUrl(message), "_blank", "noopener,noreferrer");
    setSent(true);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="glass relative overflow-hidden rounded-3xl shadow-[0_40px_100px_-30px_rgb(0_0_0_/_0.8)]">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-electric/60 to-transparent"
      />

      <div className="flex items-center gap-3 bg-[#075e54] px-6 py-4 text-white">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/15">
          <WhatsAppIcon className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold leading-tight">Suporte Alert AI</p>
          <p className="flex items-center gap-1.5 text-xs text-white/80">
            <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full animate-pulse-ring rounded-full bg-[#4ade80]" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#4ade80]" />
            </span>
            Online agora
          </p>
        </div>
      </div>

      <div className="flex min-h-[420px] flex-col justify-end gap-2 bg-[#e5ddd5] px-4 py-5 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-[85%] rounded-lg rounded-tl-none bg-white px-3.5 py-2.5 text-sm text-[#111b21] shadow"
        >
          {greeting}
          <span className="mt-1 block text-right text-[10px] text-black/40">agora</span>
        </motion.div>

        <AnimatePresence>
          {sent && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="ml-auto max-w-[85%] rounded-lg rounded-tr-none bg-[#dcf8c6] px-3.5 py-2.5 text-sm text-[#111b21] shadow"
            >
              {message}
              <span className="mt-1 block text-right text-[10px] text-black/40">agora ✓✓</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {sent ? (
          <motion.div
            key="sent"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-3 bg-[#f0f0f0] px-6 py-8 text-center"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#075e54]/10 text-[#075e54]">
              <CircleCheck className="h-6 w-6" aria-hidden="true" />
            </span>
            <p className="text-sm font-semibold text-[#111b21]">
              Você foi redirecionado ao WhatsApp!
            </p>
            <p className="max-w-sm text-xs leading-relaxed text-black/50">
              Se a conversa não abriu automaticamente, verifique se o pop-up foi bloqueado pelo
              navegador.
            </p>
            <button
              type="button"
              onClick={() => setSent(false)}
              className="mt-1 text-xs font-semibold text-[#075e54] underline underline-offset-4"
            >
              Abrir novamente
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-2 bg-[#f0f0f0] px-4 py-4 sm:px-6"
          >
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Seu nome (opcional)"
              aria-label="Seu nome"
              className="w-full rounded-full bg-white px-4 py-2.5 text-sm text-[#111b21] outline-none placeholder:text-black/40"
            />
            <button
              onClick={send}
              type="button"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#075e54] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0c6b5e]"
            >
              <Send className="h-[18px] w-[18px]" aria-hidden="true" />
              Chamar no WhatsApp
            </button>
            <p className="mt-3 px-1 text-center text-[10px] text-black/40">
              Você será redirecionado ao WhatsApp para continuar a conversa.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
